import type { TaskAction } from '@/features/contribution-kit';
import { MOD_ENTER } from '@/features/contribution-kit/use-task-shortcuts';
import type { Sector } from '@/types/theme.types';
import { fill, qualityIssueLabel } from './copy';
import type { InitialDataCopy } from './initial-data-copy';
import { estimateMinutes } from './initial-data.helpers';
import type { SubmitResult } from './initial-data.payloads';
import type { InitialDataLanguage, InitialDataStep } from './types';

/**
 * Pure view logic of the initial-data pages: which screen shows, the setup
 * figures, the bottom bar actions and the toast after a submit. Tested in
 * initial-data.view.test.ts.
 */

export type InitialDataView = 'setup' | 'loading' | 'error' | 'empty' | 'complete' | 'session';

export function resolveInitialDataView(input: {
  step: InitialDataStep;
  isLoadingQuestions: boolean;
  questionsError: boolean;
  questionCount: number;
  isComplete: boolean;
  hasCurrent: boolean;
}): InitialDataView {
  if (input.step === 'setup') return 'setup';
  if (input.isLoadingQuestions) return 'loading';
  if (input.questionsError) return 'error';
  if (input.questionCount === 0) return 'empty';
  if (input.step === 'complete' || input.isComplete || !input.hasCurrent) return 'complete';
  return 'session';
}

export const sectorName = (sectors: readonly Sector[], id: string) => sectors.find((sector) => sector.id === id)?.name ?? '';

export const languageLabel = (languages: readonly InitialDataLanguage[], code: string) => {
  const match = languages.find((item) => item.code === code);
  return match?.displayName || match?.name || code;
};

/** Setup figures, only once a language and a sector are chosen (empty list before). */
export function setupStats(
  copy: InitialDataCopy,
  input: { selected: boolean; questionCount: number; answeredCount: number; mode: 'audio' | 'text' },
) {
  if (!input.selected) return [];
  const remaining = Math.max(0, input.questionCount - input.answeredCount);
  return [
    { value: String(input.questionCount), label: copy.statQuestions },
    { value: `~${estimateMinutes(remaining, input.mode)}`, label: copy.statMinutes },
    { value: String(remaining), label: copy.statRemaining, tone: 'success' as const },
  ];
}

export function canStartSession(input: { language: string; sectorId: string; isPlaceReady: boolean }): boolean {
  return Boolean(input.language && input.sectorId && input.isPlaceReady);
}

export type RecordingControls = {
  isRecording: boolean;
  isPaused: boolean;
  hasRecording: boolean;
  onStart: () => void;
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
  onRedo: () => void;
};

export type ActionInput = {
  copy: InitialDataCopy;
  canGoPrevious: boolean;
  canSubmit: boolean;
  isSubmitting: boolean;
  isCurrentDone: boolean;
  /** A recording is running: nothing else can be done. */
  blocked: boolean;
  onPrevious: () => void;
  onSkip: () => void;
  onSubmit: () => void;
  /** Spoken answers only. */
  recording?: RecordingControls;
};

/**
 * Bottom bar actions, with the same enabled rules as the previous buttons:
 * previous and skip wait for the submit and the recording, send needs an
 * answer and a question not answered yet, record and redo are off while
 * sending or on an answered question.
 */
export function initialDataActions(input: ActionInput): TaskAction[] {
  const { copy, recording } = input;
  const busy = input.isSubmitting || input.blocked;
  const previous: TaskAction = {
    key: 'previous',
    label: copy.previous,
    icon: 'arrowLeft',
    onClick: input.onPrevious,
    disabled: !input.canGoPrevious || busy,
    shortcut: 'ArrowLeft',
  };
  const skip: TaskAction = {
    key: 'skip',
    label: copy.skip,
    icon: 'chevronRight',
    onClick: input.onSkip,
    disabled: busy,
    shortcut: 'ArrowRight',
    testId: 'initial-data-skip',
  };
  const sendDisabled = !input.canSubmit || busy || input.isCurrentDone;
  // Written answers send from the text field with Ctrl/Cmd+Enter.
  const send = (tone: TaskAction['tone']): TaskAction => ({
    key: 'send',
    label: copy.send,
    icon: 'send',
    tone,
    onClick: input.onSubmit,
    disabled: sendDisabled,
    loading: input.isSubmitting,
    shortcut: recording ? 'Enter' : MOD_ENTER,
    testId: 'initial-data-submit',
  });

  if (!recording) return [previous, skip, send('primary')];

  const recordDisabled = input.isSubmitting || input.isCurrentDone;
  if (recording.isRecording) {
    return [
      {
        key: 'pause',
        label: recording.isPaused ? copy.recordingResume : copy.recordingPause,
        icon: recording.isPaused ? 'play' : 'pause',
        onClick: recording.isPaused ? recording.onResume : recording.onPause,
        shortcut: 'p',
        testId: 'initial-data-recording-pause',
      },
      { key: 'stop', label: copy.stop, icon: 'square', tone: 'danger', onClick: recording.onStop, disabled: recordDisabled, shortcut: ' ', testId: 'initial-data-record' },
    ];
  }
  if (recording.hasRecording) {
    return [
      previous,
      skip,
      { key: 'redo', label: copy.redo, icon: 'refreshCw', onClick: recording.onRedo, disabled: recordDisabled, shortcut: 'r', testId: 'initial-data-redo' },
      send('primary'),
    ];
  }
  return [
    previous,
    skip,
    { key: 'record', label: copy.record, icon: 'mic', tone: 'primary', onClick: recording.onStart, disabled: recordDisabled, shortcut: ' ', testId: 'initial-data-record' },
    // Nothing recorded yet: send stays visible but off, as before.
    send('neutral'),
  ];
}

export type Feedback = { title: string; description?: string; variant?: 'destructive' };

/** The toast after submitting a spoken answer. */
export function audioSubmitFeedback(copy: InitialDataCopy, result: SubmitResult): Feedback {
  if (result.ok) return { title: copy.submitted };
  if (result.issues && result.issues.length > 0) {
    return {
      title: copy.qualityRejected,
      description: result.issues.map((issue) => qualityIssueLabel(copy, issue)).join(' · '),
      variant: 'destructive',
    };
  }
  return { title: copy.submitError, description: result.message, variant: 'destructive' };
}

/** The toast after submitting a written answer. */
export function textSubmitFeedback(copy: InitialDataCopy, result: SubmitResult): Feedback {
  if (result.ok) return { title: copy.submitted };
  return { title: copy.submitError, description: result.message, variant: 'destructive' };
}

/** Body of the end screen: how many answers went, or that the sector was already done. */
export function completeDescription(copy: InitialDataCopy, sentThisSession: number): string {
  return sentThisSession > 0 ? fill(copy.completeBody, { count: sentThisSession }) : copy.allDoneBody;
}
